// "Meus episódios" — /mentor/:slug/estudio (Círculo, P3, 15/10/2026).
//
// Onde o Mentor cria e edita os episódios da série, sobe o vídeo (do
// computador, arquivos de 1 a 3 GB, em pedaços que retomam — envio-video.ts),
// acompanha a conversão, assiste à prévia e publica/despublica.
//
// Só o próprio Mentor entra: a API (server/circulo/estudio.js) responde 403
// so_o_mentor para os outros e a tela mostra isso com o caminho para a página.
//
// Visual: o tema escuro do protótipo (mentor-pagina.css, preso em .cm) e as
// peças daqui em estudio.css. Ocupa a janela inteira (não a coluna de 480 px
// do app), porque o envio é feito pelo navegador do computador.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import {
  cancelarEnvio, enviarVideo, envioAtivo, mesmoArquivo, ouvirEnvio, tamanhoLegivel, tempoLegivel,
  type EnvioPendente, type EstadoEnvio,
} from './envio-video';
import './mentor-pagina.css';
import './mentor-pagina-app.css';
import './estudio.css';

const ROM = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];
const ACEITA = 'video/*,.mp4,.mov,.m4v,.mkv,.webm';
const ATUALIZAR_MS = 4000; // enquanto converte

type Situacao = 'rascunho' | 'enviando' | 'convertendo' | 'pronto' | 'erro' | 'agendado' | 'publicado';
interface EpEstudio {
  id: string; posicao: number; titulo: string; sinopse: string | null; abre_na_pagina: number | null;
  estreia_em: string | null; status: string; publicado: boolean; situacao: Situacao; texto: string;
  video_status: string | null; video_erro: string | null; duracao_s: number | null; tem_video: boolean;
  envio: EnvioPendente | null;
}

const Ic = {
  left: <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  plus: <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" /></svg>,
  up: <svg viewBox="0 0 24 24"><path d="M12 16V4M6.5 9.5L12 4l5.5 5.5M5 20h14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  warn: <svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 4l9 16H3z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /><path d="M12 10v4.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /><circle cx="12" cy="17.4" r="1.1" fill="currentColor" /></svg>,
  eye: <svg viewBox="0 0 24 24"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" stroke="currentColor" strokeWidth="1.7" fill="none" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" fill="none" /></svg>,
};

// Abaixo de 1 min, em segundos: "40 s" (o player mostra 0:40, não "1 min").
const minutos = (s?: number | null) => (!s ? '' : s < 59.5 ? `${Math.max(1, Math.round(s))} s` : `${Math.round(s / 60)} min`);
// Celular/tablet: não há o que arrastar nem "computador" para citar.
const emToque = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
const dataCurta = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
// <input type="datetime-local"> trabalha na hora local do computador.
const paraCampoData = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// O que o cartão mostra: o envio desta aba manda mais que o que o servidor sabe.
function situacaoVisivel(ep: EpEstudio, envio: EstadoEnvio | null): { s: Situacao; texto: string } {
  if (envio && envio.epId === ep.id && envioAtivo(envio)) {
    const pct = envio.tamanho ? Math.floor(envio.enviados / envio.tamanho * 100) : 0;
    return { s: 'enviando', texto: envio.fase === 'concluindo' ? 'enviando · 100%' : `enviando · ${pct}%` };
  }
  if (ep.situacao === 'publicado') return { s: 'publicado', texto: 'publicado' };
  if (ep.situacao === 'agendado') return { s: 'agendado', texto: ep.texto };
  if (ep.situacao === 'convertendo') return { s: 'convertendo', texto: 'convertendo…' };
  if (ep.situacao === 'erro') return { s: 'erro', texto: 'erro' };
  if (ep.situacao === 'pronto') return { s: 'pronto', texto: 'pronto' };
  if (ep.situacao === 'enviando') return { s: 'enviando', texto: `${ep.texto} · parado` };
  return { s: 'rascunho', texto: 'rascunho' };
}

export const MentorEstudio: React.FC<{
  session: any;
  slug: string;
  onBack: () => void;
  onAbrirPagina: () => void;
}> = ({ session, slug, onBack, onAbrirPagina }) => {
  const [dados, setDados] = useState<any>(null);
  const [erro, setErro] = useState<null | { tipo: 'negado' | 'falha' | 'sem'; texto: string }>(null);
  const [serieId, setSerieId] = useState<string | undefined>(undefined);
  const [sel, setSel] = useState<string | 'novo' | null>(null);
  const [envio, setEnvio] = useState<EstadoEnvio | null>(null);
  const sessaoRef = useRef(session);
  sessaoRef.current = session;
  const painelRef = useRef<HTMLElement>(null);
  // Uma coluna só (celular): o painel fica embaixo da lista; leva até ele.
  const abrir = (id: string) => {
    setSel(id);
    if (window.matchMedia?.('(max-width: 980px)').matches) {
      requestAnimationFrame(() => painelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    }
  };

  const carregar = useCallback(async () => {
    try {
      const d = await api.getEstudio(sessaoRef.current, slug, serieId);
      setDados(d);
      setErro(null);
    } catch (e: any) {
      if (e?.status === 403) setErro({ tipo: 'negado', texto: e.message });
      else if (e?.status === 404) setErro({ tipo: 'sem', texto: 'Este Círculo não existe.' });
      else setErro({ tipo: 'falha', texto: 'Não deu para abrir os episódios agora.' });
    }
  }, [slug, serieId]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => ouvirEnvio(setEnvio), []);

  // Terminou o envio desta tela: a lista passa a mostrar "convertendo".
  const faseAnterior = useRef<string | null>(null);
  useEffect(() => {
    const f = envio?.fase || null;
    if (f !== faseAnterior.current && (f === 'concluido' || f === 'erro' || f === 'cancelado')) carregar();
    faseAnterior.current = f;
  }, [envio?.fase, carregar]);

  const eps: EpEstudio[] = useMemo(() => dados?.episodios || [], [dados]);
  // Atualiza sozinha enquanto algum vídeo converte.
  const convertendo = eps.some((e) => e.situacao === 'convertendo');
  useEffect(() => {
    if (!convertendo) return undefined;
    const t = window.setInterval(carregar, ATUALIZAR_MS);
    return () => window.clearInterval(t);
  }, [convertendo, carregar]);

  // Sem nada escolhido: o primeiro que ainda não está no ar (ou o primeiro).
  useEffect(() => {
    if (!dados || sel) return;
    const alvo = eps.find((e) => !e.publicado && e.situacao !== 'agendado') || eps[0];
    setSel(alvo ? alvo.id : 'novo');
  }, [dados, eps, sel]);

  const topo = (
    <header className="top solid" id="top">
      <button className="cm-voltar" onClick={onBack} aria-label="Voltar">{Ic.left}</button>
      <div className="brand"><b>Forjamente</b><span>Círculo · Estúdio</span></div>
      <div className="top-r">
        <button className="btn btn-2 btn-sm" data-ver-pagina="" onClick={onAbrirPagina}>{Ic.eye}Ver a página</button>
      </div>
    </header>
  );

  if (erro || !dados) {
    return (
      <div className="cm cm-estudio" data-estudio={erro?.tipo || 'carregando'}>
        {topo}
        <main className="es-main">
          <section className="es-vazio" data-negado={erro?.tipo === 'negado' ? '' : undefined}>
            <span className="eyebrow">Meus episódios</span>
            <h1 className="serif">{erro?.tipo === 'negado' ? 'Esta área é só do Mentor' : erro ? erro.texto : 'Abrindo o estúdio…'}</h1>
            {erro?.tipo === 'negado' && <p>Aqui o Mentor sobe e publica os episódios do Círculo dele. Os episódios publicados ficam na página do Círculo.</p>}
            {erro && (
              <div className="btns">
                {erro.tipo === 'falha' && <button className="btn btn-1 btn-sm" onClick={carregar}>Tentar de novo</button>}
                <button className="btn btn-2 btn-sm" onClick={onAbrirPagina}>Ir para a página do Círculo</button>
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  const serie = dados.serie;
  const noAr = eps.filter((e) => e.publicado).length;
  const prontos = eps.filter((e) => e.situacao === 'pronto').length;
  const epSel = sel && sel !== 'novo' ? eps.find((e) => e.id === sel) || null : null;
  const envioAgora = envioAtivo(envio) ? envio : null;

  return (
    <div className="cm cm-estudio" data-estudio="ok">
      {topo}
      <main className="es-main">
        <section className="es-hero">
          <div>
            <span className="eyebrow">Estúdio do Círculo · {dados.mentor.nome}</span>
            <h1 className="serif">Meus episódios</h1>
            {serie ? (
              <p>{serie.titulo} · <em>{serie.livro.titulo}</em>{serie.livro.autor ? `, de ${serie.livro.autor}` : ''}{serie.livro.paginas ? ` · ${serie.livro.paginas} páginas` : ''}</p>
            ) : <p>Você ainda não tem uma série. A equipe do Forjamente abre a primeira com você.</p>}
            {serie && (
              <div className="es-num">
                <span><b>{eps.length}</b> {eps.length === 1 ? 'episódio' : 'episódios'}</span>
                <span><b>{noAr}</b> no ar</span>
                {/* No celular só "prontos" (o resto quebrava o rótulo em 3 linhas). */}
                <span><b>{prontos}</b> {prontos === 1 ? 'pronto' : 'prontos'}<i> para publicar</i></span>
              </div>
            )}
            {dados.series.length > 1 && (
              <div className="seg es-series" role="group" aria-label="Séries">
                {dados.series.map((s: any) => (
                  <button key={s.id} aria-pressed={s.id === serie?.id} onClick={() => { setSerieId(s.id); setSel(null); }}>{s.titulo}</button>
                ))}
              </div>
            )}
          </div>
          {serie && <button className="btn btn-ember" data-novo="" onClick={() => abrir('novo')}>{Ic.plus}Novo episódio</button>}
        </section>

        {serie && (
          <div className="es-grid">
            <div>
              {eps.length === 0 ? (
                <div className="es-lista-vazia">Nenhum episódio ainda. Comece pelo <b>Novo episódio</b>.</div>
              ) : (
                <ol className="es-lista" aria-label="Episódios da série">
                  {eps.map((e, i) => {
                    const st = situacaoVisivel(e, envio);
                    const meta = [
                      e.abre_na_pagina == null ? null : e.abre_na_pagina > 0 ? `abre na p. ${e.abre_na_pagina}` : 'abre antes da 1ª página',
                      minutos(e.duracao_s) || null,
                      e.estreia_em && !e.publicado ? `estreia ${dataCurta(e.estreia_em)}` : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <li key={e.id} className="es-ep" data-ep={e.posicao} data-situacao={st.s} aria-current={sel === e.id}>
                        <button onClick={() => abrir(e.id)}>
                          <span className="es-n">{ROM[i] || e.posicao}</span>
                          <span className="es-tx">
                            <span className="es-t">{e.titulo}</span>
                            <span className="es-meta">{meta}</span>
                            {st.s === 'erro' && <span className="es-meta erro" data-motivo="">{e.video_erro || e.texto}</span>}
                          </span>
                          <span className="es-pill" data-s={st.s}>{st.texto}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            <aside ref={painelRef} className="es-painel" data-painel={sel === 'novo' ? 'novo' : epSel ? epSel.posicao : ''}>
              {sel === 'novo' ? (
                <FormEpisodio key={`novo-${serie.id}`} paginas={serie.livro.paginas} proximo={eps.length + 1}
                  onSalvar={async (d) => {
                    const r = await api.criarEpisodio(sessaoRef.current, slug, serie.id, d);
                    await carregar();
                    setSel(r.episodio.id);
                  }}
                  onCancelar={eps.length ? () => setSel(eps[0].id) : undefined} />
              ) : epSel ? (
                <>
                  {/* O vídeo primeiro: é o que o Mentor vem fazer aqui. */}
                  <PainelVideo ep={epSel} slug={slug} sessaoRef={sessaoRef} envio={envio} outroEnviando={!!envioAgora && envioAgora.epId !== epSel.id}
                    onMudou={carregar} />
                  <FormEpisodio key={epSel.id} ep={epSel} indice={eps.indexOf(epSel)} paginas={serie.livro.paginas}
                    onSalvar={async (d) => { await api.editarEpisodio(sessaoRef.current, slug, epSel.id, d); await carregar(); }} />
                </>
              ) : null}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

/* ===== criar / editar ===== */
const FormEpisodio: React.FC<{
  ep?: EpEstudio; indice?: number; proximo?: number; paginas: number | null;
  onSalvar: (d: any) => Promise<void>; onCancelar?: () => void;
}> = ({ ep, indice, proximo, paginas, onSalvar, onCancelar }) => {
  const inicial = useMemo(() => ({
    titulo: ep?.titulo || '',
    sinopse: ep?.sinopse || '',
    abre_na_pagina: ep?.abre_na_pagina != null ? String(ep.abre_na_pagina) : '',
    estreia_em: paraCampoData(ep?.estreia_em || null),
  }), [ep]);
  const [f, setF] = useState(inicial);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState<null | { ok: boolean; t: string }>(null);
  useEffect(() => { setF(inicial); }, [inicial]);
  const mudou = JSON.stringify(f) !== JSON.stringify(inicial);
  const campo = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setF({ ...f, [k]: e.target.value }); setMsg(null); },
  });

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvando(true); setMsg(null);
    try {
      await onSalvar({
        titulo: f.titulo,
        sinopse: f.sinopse,
        abre_na_pagina: f.abre_na_pagina === '' ? null : Number(f.abre_na_pagina),
        estreia_em: f.estreia_em ? new Date(f.estreia_em).toISOString() : null,
      });
      if (ep) setMsg({ ok: true, t: 'Salvo.' });
    } catch (err: any) {
      setMsg({ ok: false, t: err?.message || 'Não deu para salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="es-form" onSubmit={salvar} data-form={ep ? 'editar' : 'novo'}>
      <span className="eyebrow">{ep ? `Episódio ${ROM[indice ?? 0] || ep.posicao}` : `Novo episódio · será o ${ROM[(proximo || 1) - 1] || proximo}`}</span>
      <h2 className="serif">{ep ? (f.titulo || 'Sem título') : 'Um episódio novo'}</h2>
      <div className="es-campo">
        <label htmlFor="es-titulo">Título</label>
        <input id="es-titulo" name="titulo" maxLength={140} placeholder="Ex.: A inversão de Munger" required {...campo('titulo')} />
      </div>
      <div className="es-campo">
        <label htmlFor="es-sinopse">Descrição curta <span>{f.sinopse.length}/600</span></label>
        <textarea id="es-sinopse" name="sinopse" maxLength={600} rows={3} placeholder="Uma ou duas frases sobre o episódio. Só aparece para quem já chegou nesta página." {...campo('sinopse')} />
      </div>
      <div className="es-campos2">
        <div className="es-campo">
          <label htmlFor="es-pagina">Abre na página</label>
          <input id="es-pagina" name="abre_na_pagina" type="number" min={0} max={paginas || undefined} inputMode="numeric" placeholder="42" required {...campo('abre_na_pagina')} />
          <p className="es-dica">{paginas ? `de ${paginas}. ` : ''}Quem ainda não chegou lá vê o episódio trancado.</p>
        </div>
        <div className="es-campo">
          <label htmlFor="es-estreia">Estreia <span>opcional</span></label>
          <input id="es-estreia" name="estreia_em" type="datetime-local" {...campo('estreia_em')} />
          <p className="es-dica">Vazio: entra no ar quando você publicar.</p>
        </div>
      </div>
      <div className="btns">
        <button className="btn btn-1 btn-sm" type="submit" data-salvar="" disabled={salvando || (!!ep && !mudou)}>
          {salvando ? 'Salvando…' : ep ? 'Salvar alterações' : 'Criar episódio'}
        </button>
        {!ep && onCancelar && <button className="btn btn-2 btn-sm" type="button" onClick={onCancelar}>Cancelar</button>}
        {msg && <span className={`es-msg ${msg.ok ? 'ok' : 'erro'}`} role="status">{msg.t}</span>}
      </div>
    </form>
  );
};

/* ===== o vídeo do episódio ===== */
const PainelVideo: React.FC<{
  ep: EpEstudio; slug: string; sessaoRef: React.MutableRefObject<any>; envio: EstadoEnvio | null;
  outroEnviando: boolean; onMudou: () => Promise<void> | void;
}> = ({ ep, slug, sessaoRef, envio, outroEnviando, onMudou }) => {
  const meuEnvio = envio && envio.epId === ep.id ? envio : null;
  const enviando = envioAtivo(meuEnvio);
  const [arrastando, setArrastando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [trocar, setTrocar] = useState(false);
  // Publicar e despublicar pedem um segundo toque, num botão em outro lugar.
  const [confirma, setConfirma] = useState<null | 'publicar' | 'despublicar'>(null);
  const [toque] = useState(emToque);
  const entrada = useRef<HTMLInputElement>(null);

  useEffect(() => { setAviso(null); setTrocar(false); setConfirma(null); }, [ep.id]);
  useEffect(() => { setConfirma(null); }, [ep.situacao, ep.publicado]);

  const escolher = (arquivo: File | undefined | null) => {
    if (!arquivo) return;
    if (outroEnviando) { setAviso('Outro vídeo está sendo enviado. Espere terminar.'); return; }
    if (arquivo.type && !arquivo.type.startsWith('video/')) { setAviso('Escolha um arquivo de vídeo (MP4 ou MOV).'); return; }
    setAviso(null);
    setTrocar(false);
    enviarVideo({ slug, epId: ep.id, arquivo, pendente: ep.envio, token: () => sessaoRef.current?.accessToken })
      .catch((e) => setAviso(e?.message || 'O envio falhou.'));
  };

  const publicar = async (sim: boolean) => {
    setPublicando(true); setAviso(null); setConfirma(null);
    try { await api.publicarEpisodio(sessaoRef.current, slug, ep.id, sim); await onMudou(); } catch (e: any) { setAviso(e?.message || 'Não deu agora.'); } finally { setPublicando(false); }
  };

  /* ----- enviando ----- */
  if (enviando && meuEnvio) {
    const pct = meuEnvio.tamanho ? Math.min(100, meuEnvio.enviados / meuEnvio.tamanho * 100) : 0;
    const falta = meuEnvio.velocidade > 0 ? (meuEnvio.tamanho - meuEnvio.enviados) / meuEnvio.velocidade : NaN;
    const linha = meuEnvio.fase === 'preparando' ? 'Preparando o envio…'
      : meuEnvio.fase === 'reconectando' ? 'A conexão caiu. Tentando de novo sozinho…'
        : meuEnvio.fase === 'concluindo' ? 'Chegou inteiro. Conferindo o arquivo…'
          : `${(meuEnvio.velocidade / 1024 ** 2).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB/s${tempoLegivel(falta) ? ` · faltam ${tempoLegivel(falta)}` : ''}`;
    return (
      <section className="es-video" data-video="enviando">
        <div className="es-vh"><span className="eyebrow">O vídeo</span><span className="es-arq">{meuEnvio.nome}</span></div>
        <div className="es-prog">
          <span className="es-pct serif" data-pct={Math.floor(pct)}>{Math.floor(pct)}<small>%</small></span>
          <div className="es-prog-tx">
            <span data-bytes="">{tamanhoLegivel(meuEnvio.enviados)} de {tamanhoLegivel(meuEnvio.tamanho)}</span>
            <span data-ritmo="" className={meuEnvio.fase === 'reconectando' ? 'aten' : ''}>{linha}</span>
          </div>
        </div>
        <div className="es-barra" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(pct)}><i style={{ width: `${pct}%` }} /></div>
        {meuEnvio.retomadoDe > 0 && <p className="es-dica" data-retomado="">Retomado de onde parou: {tamanhoLegivel(meuEnvio.retomadoDe)} já estavam no servidor.</p>}
        <div className="es-aviso" data-nao-feche="">{Ic.warn}<span><b>Não feche esta aba</b> até o envio terminar. Se a rede cair, ele continua sozinho; se a aba fechar, escolha o mesmo arquivo de novo e ele segue do ponto em que parou.</span></div>
        <div className="btns"><button className="btn btn-2 btn-sm" data-cancelar="" onClick={cancelarEnvio}>Cancelar envio</button></div>
      </section>
    );
  }

  /* ----- convertendo ----- */
  if (ep.situacao === 'convertendo') {
    return (
      <section className="es-video" data-video="convertendo">
        <div className="es-vh"><span className="eyebrow">O vídeo</span></div>
        <div className="es-conv">
          <span className="es-giro" aria-hidden="true" />
          <div><h3 className="serif">Convertendo…</h3>
            <p>Leva mais ou menos a duração do vídeo. Esta tela se atualiza sozinha; pode até fechar a aba, a conversão continua no servidor.</p></div>
        </div>
      </section>
    );
  }

  const seletor = (
    <div className={`es-drop ${arrastando ? 'over' : ''}`} data-drop=""
      onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => { e.preventDefault(); setArrastando(false); escolher(e.dataTransfer.files?.[0]); }}>
      <span className="es-drop-ic">{Ic.up}</span>
      {ep.envio && !meuEnvio?.fase?.match(/cancelado/) ? (
        <p data-pendente="">Há um envio pela metade: <b>{ep.envio.nome || 'arquivo'}</b>, {Math.floor(ep.envio.offset / ep.envio.tamanho * 100)}% ({tamanhoLegivel(ep.envio.offset)} de {tamanhoLegivel(ep.envio.tamanho)}).<br />
          Escolha <b>o mesmo arquivo</b> para continuar de onde parou.</p>
      ) : (
        <p>{toque ? 'Escolha o vídeo do episódio.' : 'Arraste o vídeo para cá ou escolha no computador.'}<br /><span>Envie o original (MP4 ou MOV, até 8 GB); a versão para assistir em 720p é feita aqui.</span></p>
      )}
      <button className="btn btn-1 btn-sm" type="button" data-escolher="" onClick={() => entrada.current?.click()} disabled={outroEnviando}>
        {ep.envio ? 'Escolher o arquivo para continuar' : toque ? 'Escolher vídeo' : 'Escolher o vídeo'}
      </button>
      <input ref={entrada} type="file" accept={ACEITA} hidden data-arquivo=""
        onChange={(e) => { escolher(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );

  const avisos = (
    <>
      {meuEnvio?.fase === 'erro' && <p className="es-msg erro" data-envio-erro="">{meuEnvio.erro}</p>}
      {meuEnvio?.fase === 'cancelado' && <p className="es-msg">Envio cancelado.</p>}
      {aviso && <p className="es-msg erro" role="alert">{aviso}</p>}
      {outroEnviando && <p className="es-msg">Outro episódio está enviando vídeo agora; um envio por vez.</p>}
    </>
  );

  /* ----- com vídeo pronto: prévia + publicar ----- */
  if (ep.tem_video && ep.video_status === 'pronto' && !trocar) {
    const noAr = ep.publicado || ep.situacao === 'agendado';
    const futura = ep.estreia_em && new Date(ep.estreia_em) > new Date();
    return (
      <section className="es-video" data-video="pronto">
        <div className="es-vh"><span className="eyebrow">O vídeo{ep.duracao_s ? ` · ${minutos(ep.duracao_s)}` : ''}</span>
          <span className="es-pill" data-s={ep.situacao}>{ep.situacao === 'agendado' ? ep.texto : ep.publicado ? 'publicado' : 'pronto'}</span></div>
        <Previa key={`${ep.id}-${ep.duracao_s}`} slug={slug} epId={ep.id} sessaoRef={sessaoRef} />
        {avisos}
        {confirma === 'publicar' ? (
          // A pergunta empurra os botões para baixo e o "Sim" fica à direita: um toque duplo não publica.
          <div className="es-confirma" data-confirma="publicar" role="alertdialog" aria-label="Confirmar a publicação">
            <p>{futura ? <><b>Agendar para {dataHora(ep.estreia_em!)}?</b> Ele entra no ar sozinho nessa hora para quem assina.</>
              : <><b>Publicar agora?</b> Quem assina já vai ver.</>}</p>
            <div className="btns">
              <button className="btn btn-2 btn-sm" data-nao-confirmar="" onClick={() => setConfirma(null)}>Cancelar</button>
              <button className="btn btn-ember btn-sm" data-confirmar="" disabled={publicando} onClick={() => publicar(true)}>{futura ? 'Sim, agendar' : 'Sim, publicar'}</button>
            </div>
          </div>
        ) : (
          <div className="btns">
            {!noAr && (
              <button className="btn btn-ember btn-sm" data-publicar="" disabled={publicando} onClick={() => setConfirma('publicar')}>
                {futura ? `Agendar para ${dataHora(ep.estreia_em!)}` : 'Publicar'}
              </button>
            )}
            <button className="btn btn-2 btn-sm" data-trocar="" onClick={() => setTrocar(true)} disabled={outroEnviando}>Trocar o vídeo</button>
          </div>
        )}
        <p className="es-dica">{ep.publicado ? 'No ar: quem assina e já chegou na página vê o episódio.'
          : ep.situacao === 'agendado' ? 'Agendado: entra no ar sozinho na data da estreia.'
            : futura ? 'Ao agendar, ele entra no ar sozinho na data da estreia.' : 'Ao publicar, ele entra no ar agora para quem assina.'}</p>
        {/* Despublicar: embaixo, à direita, longe de onde ficava o "Publicar"/"Sim, publicar". */}
        {noAr && (confirma === 'despublicar' ? (
          <div className="es-confirma es-confirma-dir" data-confirma="despublicar" role="alertdialog" aria-label="Confirmar">
            <p>{ep.publicado ? <><b>Despublicar?</b> Quem assina deixa de ver o episódio.</> : <><b>Cancelar o agendamento?</b> Ele não entra no ar na estreia.</>}</p>
            <div className="btns">
              <button className="btn btn-2 btn-sm" data-confirmar="" disabled={publicando} onClick={() => publicar(false)}>{ep.publicado ? 'Sim, despublicar' : 'Sim, cancelar'}</button>
              <button className="btn btn-1 btn-sm" data-nao-confirmar="" onClick={() => setConfirma(null)}>{ep.publicado ? 'Manter no ar' : 'Manter agendado'}</button>
            </div>
          </div>
        ) : (
          <div className="es-tirar"><button className="es-link" data-despublicar="" disabled={publicando} onClick={() => setConfirma('despublicar')}>Despublicar</button></div>
        ))}
      </section>
    );
  }

  /* ----- sem vídeo, com erro ou trocando ----- */
  return (
    <section className="es-video" data-video={ep.situacao === 'erro' ? 'erro' : 'sem'}>
      <div className="es-vh"><span className="eyebrow">O vídeo</span>
        {trocar && <button className="es-link" onClick={() => setTrocar(false)}>manter o vídeo atual</button>}</div>
      {ep.situacao === 'erro' && !meuEnvio && (
        <div className="es-erro" data-erro="">{Ic.warn}<span><b>A conversão falhou.</b> {ep.video_erro || ''} Envie o vídeo de novo.</span></div>
      )}
      {trocar && ep.publicado && <p className="es-dica">O vídeo atual continua no ar até o novo ficar pronto.</p>}
      {seletor}
      {avisos}
    </section>
  );
};

// A prévia: o mesmo link assinado que o leitor recebe (o Mentor sempre pode).
// A moldura toma a proporção do vídeo: gravado em pé, fica em pé (sem tarja preta).
const Previa: React.FC<{ slug: string; epId: string; sessaoRef: React.MutableRefObject<any> }> = ({ slug, epId, sessaoRef }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tam, setTam] = useState<null | { w: number; h: number }>(null);
  useEffect(() => {
    let vivo = true;
    api.getEpisodioVideo(sessaoRef.current, slug, epId)
      .then((r) => { if (vivo) setSrc(r.caminho || r.url); })
      .catch((e: any) => { if (vivo) setErro(e?.message || 'Não deu para abrir a prévia.'); });
    return () => { vivo = false; };
  }, [slug, epId, sessaoRef]);
  const formato = !tam ? undefined : tam.h > tam.w * 1.05 ? 'vertical' : tam.w > tam.h * 1.05 ? 'horizontal' : 'quadrado';
  return (
    <div className="es-previa" data-previa="" data-formato={formato} style={tam ? { aspectRatio: `${tam.w} / ${tam.h}` } : undefined}>
      {src ? <video src={src} controls playsInline preload="metadata"
        onLoadedMetadata={(e) => { const v = e.currentTarget; if (v.videoWidth && v.videoHeight) setTam({ w: v.videoWidth, h: v.videoHeight }); }} />
        : <div className="es-previa-vazia">{erro || 'Carregando a prévia…'}</div>}
    </div>
  );
};
